import { ReactElement } from "react"
import styled from "@emotion/styled"
import { useTranslation } from "../TranslationProvider"
import { SpecialTitle } from "../App"

export default function TrustedCompanies(): ReactElement {
  const { t } = useTranslation()

  const companies = [
    {
      id: 1,
      image: "photos/showbird.jpg",
    },
    // {
    //   id: 2,
    //   image: "photos/logo.jpg",
    // },
    {
      id: 3,
      image: "photos/love-disco-cruise.jpg",
    },
    {
      id: 4,
      image: "photos/cfe.jpg",
    },
    {
      id: 5,
      image: "photos/plunck.jpg",
    },
    {
      id: 6,
      image: "photos/uptown.jpg",
    },
  ]

  return (
    <SectionWrapper>
      <SectionContent>
        <SpecialTitle>{t("trustedCompanies.title")}</SpecialTitle>
        <SpecialTitle $fontSize={1.2} $color="var(--white)">
          {t("trustedCompanies.subtitle")}
        </SpecialTitle>

        <ScrollContainer>
          <CompaniesGrid>
            {companies.map((company) => (
              <CompanyCard
                key={company.id}
                className="company-card"
                href={t(`trustedCompanies.company${company.id}.url`)}
                target="_blank"
                rel="noopener noreferrer">
                <CompanyImageWrapper>
                  <CompanyImage
                    src={company.image}
                    alt={t(`trustedCompanies.company${company.id}.name`)}
                    loading="lazy"
                    className="company-image"
                  />
                </CompanyImageWrapper>
                <CompanyInfo>
                  <CompanyName>
                    {t(`trustedCompanies.company${company.id}.name`)}
                  </CompanyName>
                  <CompanyDescription>
                    {t(`trustedCompanies.company${company.id}.description`)}
                  </CompanyDescription>
                </CompanyInfo>
              </CompanyCard>
            ))}
          </CompaniesGrid>
        </ScrollContainer>
      </SectionContent>
    </SectionWrapper>
  )
}

const SectionWrapper = styled.section`
  width: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(75, 46, 110, 0.15) 20%,
    rgba(75, 46, 110, 0.25) 100%
  );
  padding: 2rem 0 1rem;
  border-top: 1px solid var(--special);
  position: relative;

  @media only screen and (min-width: 500px) {
    padding: 4rem 0;
  }
`

const SectionContent = styled.div`
  max-width: 120rem;
  margin: 0 auto;
  padding: 0;

  @media only screen and (min-width: 500px) {
    padding: 0 2rem;
  }
`

const ScrollContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-color: var(--special) rgba(75, 46, 110, 0.3);
  scroll-snap-type: x mandatory;
  scroll-padding: 0 1rem;
  scrollbar-width: none;
  margin-bottom: 0;

  &::-webkit-scrollbar {
    display: none;
  }

  &::-webkit-scrollbar-track {
    background: rgba(75, 46, 110, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--special);
    border-radius: 4px;

    &:hover {
      background: hsl(39, 79%, 58%);
    }
  }

  /* Hide scrollbar on mobile for cleaner look */
  @media only screen and (min-width: 500px) {
    scrollbar-width: thin;
    margin-bottom: 1rem;

    &::-webkit-scrollbar {
      height: 8px;
    }
  }
`

const CompaniesGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  min-width: min-content;

  @media only screen and (min-width: 500px) {
    gap: 2rem;
    padding: 2rem 0;
  }
`

const CompanyCard = styled.a`
  display: flex;
  flex-direction: column;
  width: calc(100vw - 3rem);
  min-width: calc(100vw - 3rem);
  background: rgba(0, 0, 0, 0.6);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(231, 158, 38, 0.2);
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  scroll-snap-align: center;
  scroll-snap-stop: always;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
  }

  @media only screen and (min-width: 500px) {
    width: 280px;
    min-width: 280px;

    &:hover {
      transform: translateY(-8px);
      border-color: var(--special);
      box-shadow: 0 8px 24px rgba(231, 158, 38, 0.3);

      .company-image {
        transform: scale(1.1);
      }
    }
  }
`

const CompanyImageWrapper = styled.div`
  width: 100%;
  height: calc(100vw - 3rem);
  overflow: hidden;
  background: rgba(75, 46, 110, 0.3);
  position: relative;

  @media only screen and (min-width: 500px) {
    height: 280px;
  }
`

const CompanyImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`

const CompanyInfo = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;

  @media only screen and (min-width: 500px) {
    padding: 1.5rem;
  }
`

const CompanyName = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--special);
  margin: 0;
  text-shadow: 0 0 1rem rgba(231, 158, 38, 0.3);

  @media only screen and (min-width: 500px) {
    font-size: 1.3rem;
  }
`

const CompanyDescription = styled.p`
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--white);
  margin: 0;
  opacity: 0.85;

  @media only screen and (max-width: 768px) {
    font-size: 0.95rem;
  }
`
